import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertTriangle, RefreshCw, RotateCcw, Link2, Settings2, CheckCircle2,
  Clock, ExternalLink, Loader2, Ban, DownloadCloud,
} from "lucide-react";
import {
  listPortalBankConnections,
  setPortalBankAccountExclusions,
  reconnectPortalBank,
  refreshPortalBankStatus,
  syncPortalBankNow,
  listPortalBankSyncRuns,
  getPortalBankSettings,
  setPortalBankSettings,
  deletePortalBankConnection,
  discoverPortalBankConnections,
} from "@/lib/api";
import { toast } from "sonner";

const STATUS_LABELS = {
  NOT_CONNECTED: { label: "Not connected", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  CR: { label: "Awaiting consent", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  GC: { label: "Giving consent", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  UA: { label: "Authenticating", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  SA: { label: "Selecting accounts", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  GA: { label: "Granting access", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  LN: { label: "Linked", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  EX: { label: "Expired", cls: "bg-red-100 text-red-700 border-red-300" },
  RJ: { label: "Rejected", cls: "bg-red-100 text-red-700 border-red-300" },
  SU: { label: "Suspended", cls: "bg-red-100 text-red-700 border-red-300" },
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const daysLeftClass = (days) => {
  if (days == null) return "text-slate-500";
  if (days <= 7) return "text-red-700 font-semibold";
  if (days <= 30) return "text-amber-700 font-medium";
  return "text-emerald-700";
};

function SettingsDialog({ open, onClose, onSaved, region = "UK" }) {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    getPortalBankSettings().then(setSettings).catch(() => toast.error("Failed to load settings"));
  }, [open]);

  const save = async () => {
    if (!settings) return;
    setBusy(true);
    try {
      const saved = await setPortalBankSettings({
        enabled: settings.enabled,
        sync_hour: Number(settings.sync_hour),
        sync_minute: Number(settings.sync_minute || 0),
        alert_email: settings.alert_email,
        slack_channel: settings.slack_channel || null,
        alert_threshold_days: Number(settings.alert_threshold_days || 7),
        ...(region === "AUS" ? {
          midday_enabled: !!settings.midday_enabled,
          midday_hour: Number(settings.midday_hour ?? 12),
          midday_minute: Number(settings.midday_minute ?? 0),
        } : {}),
      });
      toast.success(`Sync scheduled for ${String(saved.sync_hour).padStart(2, "0")}:${String(saved.sync_minute).padStart(2, "0")} London`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save settings");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent data-testid="portal-bank-settings-dialog">
        <DialogHeader>
          <DialogTitle>Bank Feed Settings</DialogTitle>
          <DialogDescription>
            Native GoCardless Open Banking ingestion. Runs once daily to pull yesterday&apos;s cleared
            payments into the Loan Repayments wizard and mirror them to Airtable.
          </DialogDescription>
        </DialogHeader>
        {!settings ? (
          <div className="py-8 text-center text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Automatic daily sync</Label>
                <p className="text-xs text-slate-500">Uncheck to pause the cron (manual &quot;Sync Now&quot; still works)</p>
              </div>
              <Switch
                checked={!!settings.enabled}
                onCheckedChange={(v) => setSettings({ ...settings, enabled: !!v })}
                data-testid="portal-bank-enabled-toggle"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="sync_hour" className="text-sm">Sync hour (London, 0–23)</Label>
                <Input
                  id="sync_hour" type="number" min={0} max={23}
                  value={settings.sync_hour ?? 5}
                  onChange={(e) => setSettings({ ...settings, sync_hour: e.target.value })}
                  data-testid="portal-bank-sync-hour-input"
                />
              </div>
              <div>
                <Label htmlFor="sync_minute" className="text-sm">Sync minute</Label>
                <Input
                  id="sync_minute" type="number" min={0} max={59}
                  value={settings.sync_minute ?? 0}
                  onChange={(e) => setSettings({ ...settings, sync_minute: e.target.value })}
                  data-testid="portal-bank-sync-minute-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="alert_email" className="text-sm">Alert email (renewal reminders)</Label>
              <Input
                id="alert_email" type="email"
                value={settings.alert_email || ""}
                onChange={(e) => setSettings({ ...settings, alert_email: e.target.value })}
                data-testid="portal-bank-alert-email-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="slack_channel" className="text-sm">Slack channel (blank = default)</Label>
                <Input
                  id="slack_channel"
                  placeholder="#ops"
                  value={settings.slack_channel || ""}
                  onChange={(e) => setSettings({ ...settings, slack_channel: e.target.value })}
                  data-testid="portal-bank-slack-channel-input"
                />
              </div>
              <div>
                <Label htmlFor="alert_threshold" className="text-sm">Renewal alert days</Label>
                <Input
                  id="alert_threshold" type="number" min={1} max={30}
                  value={settings.alert_threshold_days ?? 7}
                  onChange={(e) => setSettings({ ...settings, alert_threshold_days: e.target.value })}
                  data-testid="portal-bank-alert-days-input"
                />
              </div>
            </div>
            {region === "AUS" && (
              <div className="rounded-md border border-slate-200 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Mid-day sync (AUS / Ebury)</Label>
                    <p className="text-xs text-slate-500">Pulls TODAY&apos;s credits — Australia&apos;s day is over by UK midday</p>
                  </div>
                  <Switch
                    checked={!!settings.midday_enabled}
                    onCheckedChange={(v) => setSettings({ ...settings, midday_enabled: !!v })}
                    data-testid="portal-bank-midday-toggle"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="midday_hour" className="text-sm">Mid-day hour (London)</Label>
                    <Input
                      id="midday_hour" type="number" min={0} max={23}
                      value={settings.midday_hour ?? 12}
                      onChange={(e) => setSettings({ ...settings, midday_hour: e.target.value })}
                      data-testid="portal-bank-midday-hour-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="midday_minute" className="text-sm">Mid-day minute</Label>
                    <Input
                      id="midday_minute" type="number" min={0} max={59}
                      value={settings.midday_minute ?? 0}
                      onChange={(e) => setSettings({ ...settings, midday_minute: e.target.value })}
                      data-testid="portal-bank-midday-minute-input"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy || !settings} data-testid="portal-bank-settings-save">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PortalBankFeedStrip({ region = "UK", syncToday = false, title = "Bank Feeds" }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pickDate, setPickDate] = useState("");
  const [reconnectingId, setReconnectingId] = useState(null);
  const [discovering, setDiscovering] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([
        listPortalBankConnections(region),
        listPortalBankSyncRuns(1, region === "UK" ? null : region),
      ]);
      setConnections(c?.connections || []);
      setLastRun((r?.runs || [])[0] || null);
    } catch (e) {
      // silent — strip should never block the page
    } finally {
      setLoading(false);
    }
  }, [region]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh status after we come back from a bank consent redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inst = params.get("bank_connected");
    if (!inst) return;
    (async () => {
      try {
        const cur = await listPortalBankConnections();
        const match = (cur?.connections || []).find(c => c.institution_id === inst && c.requisition_id);
        if (match?.requisition_id) {
          await refreshPortalBankStatus(match.requisition_id);
          toast.success(`${match.display_name} — bank consent received`);
          refresh();
        }
      } catch (e) { /* noop */ }
      // Clean the URL param so refresh doesn't re-fire
      params.delete("bank_connected");
      const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
      window.history.replaceState({}, "", clean);
    })();
  }, [refresh]);

  const handleReconnect = async (institution_id) => {
    setReconnectingId(institution_id);
    try {
      // Send GC back to THIS page after consent so the auto-refresh fires
      // (the backend default redirect can't know the domain the user is on)
      const redirectBack = `${window.location.origin}${window.location.pathname}?bank_connected=${institution_id}`;
      const res = await reconnectPortalBank(institution_id, redirectBack);
      if (res?.auth_link) {
        // Open in a new tab so the operator can complete the consent flow
        window.open(res.auth_link, "_blank", "noopener");
        toast.success(`Reconnect link opened for ${institution_id}. Complete the consent in the new tab — the status here updates automatically when you return.`);
        refresh();
      } else {
        toast.error("No consent link returned by GoCardless");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconnect failed");
    } finally {
      setReconnectingId(null);
    }
  };

  const handleRefreshStatus = async (requisition_id) => {
    try {
      await refreshPortalBankStatus(requisition_id);
      toast.success("Status refreshed");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to refresh status");
    }
  };

  const handleToggleAccount = async (conn, account) => {
    const current = (conn.accounts || []).filter((a) => a.excluded).map((a) => a.account_id);
    const next = account.excluded
      ? current.filter((id) => id !== account.account_id)
      : [...current, account.account_id];
    try {
      await setPortalBankAccountExclusions(conn.requisition_id, next);
      toast.success(account.excluded
        ? `${account.label || "Account"} included in sync`
        : `${account.label || "Account"} excluded from sync`);
      refresh();
    } catch (e) {
      toast.error("Failed to update account sync setting");
    }
  };

  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  // Poll the run history until the run we just triggered finishes, then toast
  // the outcome (new-row count, errors, or the multi-worker skip reason).
  const reportRunOutcome = async (clickedAt, syncRegion) => {
    let finished = false;
    for (let i = 0; i < 9 && !finished; i++) {
      await new Promise(r => setTimeout(r, 4000));
      try {
        const res = await listPortalBankSyncRuns(1, syncRegion);
        const run = res?.runs?.[0];
        if (run?.started_at && new Date(run.started_at) >= new Date(clickedAt.getTime() - 5000) && run.finished_at) {
          finished = true;
          if (run.skipped) {
            toast.info(run.reason || "Sync skipped — this date range was already synced in the last 10 minutes.", { duration: 10000 });
          } else {
            const n = run.total_new_rows ?? 0;
            const errs = (run.errors || []).length;
            if (errs) toast.warning(`Sync finished with ${errs} error${errs === 1 ? "" : "s"} — ${n} new payment${n === 1 ? "" : "s"} pulled. Open Bank Feeds for details.`, { duration: 12000 });
            else toast.success(`Sync complete — ${n} new payment${n === 1 ? "" : "s"} pulled.`);
          }
        }
      } catch { /* keep polling */ }
    }
    if (!finished) {
      toast.info("Sync is still running in the background — refresh the page in a minute to see the new payments.", { duration: 10000 });
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    const clickedAt = new Date();
    const syncRegion = syncToday ? region : (region === "UK" ? null : region);
    try {
      const today = ymd(clickedAt);
      if (syncToday) {
        await syncPortalBankNow(today, today, region);
      } else {
        // Manual sync covers yesterday AND today — the 5am cron already did
        // yesterday, but ops click this to pull payments received TODAY.
        const y = new Date(clickedAt); y.setDate(y.getDate() - 1);
        await syncPortalBankNow(ymd(y), today, syncRegion);
      }
      toast.success(syncToday
        ? "Sync started — pulling today's payments…"
        : "Sync started — pulling yesterday's and today's payments…");
      await reportRunOutcome(clickedAt, syncRegion);
      await refresh();
      setSyncing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
      setSyncing(false);
    }
  };

  const handleSyncDay = async () => {
    if (!pickDate) { toast.error("Pick a date to pull first"); return; }
    setSyncing(true);
    const clickedAt = new Date();
    const syncRegion = region === "UK" ? null : region;
    try {
      await syncPortalBankNow(pickDate, pickDate, syncRegion);
      toast.success(`Sync started — pulling payments for ${new Date(pickDate + "T00:00:00").toLocaleDateString("en-GB")}…`);
      await reportRunOutcome(clickedAt, syncRegion);
      await refresh();
      setSyncing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
      setSyncing(false);
    }
  };

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const res = await discoverPortalBankConnections();
      const n = res?.imported_count || 0;
      if (n > 0) {
        toast.success(`Imported ${n} existing bank ${n === 1 ? "connection" : "connections"} from GoCardless.`);
      } else if ((res?.skipped_count || 0) > 0) {
        toast.info("No new connections to import — all live requisitions are already linked.");
      } else {
        toast.info("No live requisitions found on GoCardless. Use Reconnect to create one.");
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to discover existing connections");
    } finally {
      setDiscovering(false);
    }
  };

  const handleArchive = async (requisition_id) => {
    if (!requisition_id) return;
    if (!window.confirm("Archive this bank connection? You'll need to reconnect to resume syncing.")) return;
    try {
      await deletePortalBankConnection(requisition_id);
      toast.success("Connection archived");
      refresh();
    } catch (e) {
      toast.error("Failed to archive");
    }
  };

  const anyNeedsRenewal = connections.some(c => c.needs_reconnect);
  const anyExpiredOrRejected = connections.some(c => ["EX", "RJ", "SU"].includes(c.status));
  const anyNotConnected = connections.some(c => c.status === "NOT_CONNECTED");

  return (
    <div className="space-y-2" data-testid="portal-bank-feed-strip">
      {anyNeedsRenewal && (
        <div
          className={`rounded-lg border px-4 py-2 flex items-start gap-3 ${
            anyExpiredOrRejected ? "bg-red-50 border-red-300" : "bg-amber-50 border-amber-300"
          }`}
          data-testid="portal-bank-renewal-banner"
        >
          <AlertTriangle className={`w-5 h-5 mt-0.5 ${anyExpiredOrRejected ? "text-red-600" : "text-amber-600"}`} />
          <div className="text-sm flex-1">
            <div className={`font-semibold ${anyExpiredOrRejected ? "text-red-800" : "text-amber-800"}`}>
              Bank consent needs renewal
            </div>
            <div className={anyExpiredOrRejected ? "text-red-700" : "text-amber-700"}>
              Open Banking consent is a rolling 90-day permission. Click <b>Reconnect</b> on any
              flagged bank below before it fully expires or overnight syncs will pause.
              {anyNotConnected && (
                <> If you already have live requisitions on GoCardless (e.g. from the previous
                Make.com flow), click <b>Import existing</b> to adopt them here — no
                re-authorisation needed.</>
              )}
            </div>
          </div>
          {anyNotConnected && (
            <Button
              size="sm" variant="outline"
              onClick={handleDiscover}
              disabled={discovering}
              data-testid="portal-bank-discover-btn"
            >
              {discovering
                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <DownloadCloud className="w-3.5 h-3.5 mr-1" />}
              Import existing
            </Button>
          )}
        </div>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-700">{title}</span>
              <span className="text-xs text-slate-500">
                {lastRun
                  ? `· last sync ${fmtDateTime(lastRun.finished_at || lastRun.started_at)} · +${lastRun.total_new_rows || 0} new rows`
                  : "· never synced"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1" title="Pull a specific day's payments (e.g. backfill a missed date)">
                <input
                  type="date"
                  value={pickDate}
                  onChange={(e) => setPickDate(e.target.value)}
                  className="h-8 rounded-md border border-slate-200 px-2 text-xs text-slate-700"
                  data-testid="portal-bank-pick-date-input"
                />
                <Button
                  size="sm" variant="outline" onClick={handleSyncDay} disabled={syncing || loading || !pickDate}
                  data-testid="portal-bank-sync-day-btn"
                >
                  Pull day
                </Button>
              </div>
              <Button
                size="sm" variant="outline" onClick={handleSyncNow} disabled={syncing || loading}
                data-testid="portal-bank-sync-now-btn"
              >
                {syncing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Sync now
              </Button>
              <Button
                size="sm" variant="ghost" onClick={() => setSettingsOpen(true)}
                data-testid="portal-bank-settings-btn"
                title="Bank feed settings"
              >
                <Settings2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {loading && (
              <div className="col-span-full text-xs text-slate-500 py-4 text-center">Loading bank feeds…</div>
            )}
            {!loading && connections.map((conn) => {
              const st = STATUS_LABELS[conn.status] || STATUS_LABELS.NOT_CONNECTED;
              const isLinked = conn.status === "LN";
              const needsAction = conn.needs_reconnect;
              return (
                <div
                  key={conn.institution_id}
                  className={`rounded-md border p-2 flex items-center justify-between gap-2 ${
                    needsAction ? "border-amber-300 bg-amber-50/40" : "border-slate-200 bg-white"
                  }`}
                  data-testid={`portal-bank-card-${conn.institution_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-slate-800">{conn.display_name}</span>
                      <Badge variant="outline" className={`text-xs ${st.cls}`}>{st.label}</Badge>
                      {isLinked && (
                        <span className={`text-xs ${daysLeftClass(conn.days_left)}`}>
                          <Clock className="w-3 h-3 inline-block mr-0.5" />
                          {conn.days_left != null ? `${conn.days_left}d left` : "—"}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      {conn.accounts?.length ? (
                        <span className="inline-flex flex-wrap gap-1 align-middle">
                          {conn.accounts.map((a) => (
                            <button
                              key={a.account_id}
                              type="button"
                              onClick={() => handleToggleAccount(conn, a)}
                              title={`${a.iban || a.bban || a.account_id} — click to ${a.excluded ? "include in" : "exclude from"} the sync`}
                              data-testid={`portal-bank-account-toggle-${a.account_id}`}
                              className={`px-1.5 py-0.5 rounded border text-[11px] leading-none transition-colors ${
                                a.excluded
                                  ? "bg-slate-100 text-slate-400 border-slate-200 line-through"
                                  : "bg-emerald-50 text-emerald-700 border-emerald-200"
                              }`}
                            >
                              {a.label || "?"}{(a.bban || a.iban) ? ` …${(a.bban || a.iban).slice(-4)}` : ""}
                            </button>
                          ))}
                        </span>
                      ) : "No accounts linked"}
                      {conn.last_sync_at && (
                        <> · synced {fmtDateTime(conn.last_sync_at)}</>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {isLinked && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => handleRefreshStatus(conn.requisition_id)}
                        title="Refresh status from GoCardless"
                        data-testid={`portal-bank-refresh-status-${conn.institution_id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {isLinked && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => handleArchive(conn.requisition_id)}
                        title="Archive this connection"
                        data-testid={`portal-bank-archive-${conn.institution_id}`}
                      >
                        <Ban className="w-3.5 h-3.5 text-slate-500" />
                      </Button>
                    )}
                    {needsAction ? (
                      <Button
                        size="sm"
                        variant={["EX","RJ","SU"].includes(conn.status) ? "destructive" : "default"}
                        onClick={() => handleReconnect(conn.institution_id)}
                        disabled={reconnectingId === conn.institution_id}
                        data-testid={`portal-bank-reconnect-${conn.institution_id}`}
                      >
                        {reconnectingId === conn.institution_id
                          ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                          : <ExternalLink className="w-3.5 h-3.5 mr-1" />}
                        Reconnect
                      </Button>
                    ) : (
                      <span className="text-xs text-emerald-700 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> healthy
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSaved={refresh}
        region={region}
      />
    </div>
  );
}
